import { ReactNode, useEffect, useState } from 'react';
import { Empty, Skeleton, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { AxiosError, AxiosResponse } from 'axios';
import Icon from '@ant-design/icons';
import { UseQueryResult } from '@tanstack/react-query';

import {
  IAdmin,
  IExercise,
  IExerciseGroup,
  IGuideline,
  IGuidelineChapter,
  IGuidelineChapterTiny,
  IPageAbleResponse,
  ISetting,
  IVideo,
  IVideoFile,
  TableFilter,
} from '@chess-class/sandbox';

import { ChessKnightIcon } from '~/components/atoms/icons';
import { TableSizeChanger } from '~/components/organisms/adminTable/organismComponents/TableSizeChanger';
import { TableHeader } from '~/components/organisms/adminTableSelfManaged/organismComponents/TableHeader';

type IAdminTableContent =
  | ISetting
  | IExercise
  | IExerciseGroup
  | IAdmin
  | IVideo
  | IVideoFile
  | IGuideline
  | IGuidelineChapterTiny
  | IGuidelineChapter;

type AdminTableType<T extends IAdminTableContent> = {
  columns: ColumnsType<T>;
  emptyDescription?: ReactNode;
  filters?: TableFilter['filters'];
  position?: ('topLeft' | 'bottomLeft')[];
  query: (params) => UseQueryResult<AxiosResponse<IPageAbleResponse<T>>, AxiosError>;
  queryData?: object;
  rowClassName?: (record: T, row: number) => string;
  rowKey?: keyof T;
};

export const AdminTableSelfManaged = <T extends IAdminTableContent>({
  columns,
  emptyDescription = 'Нет данных',
  filters,
  position,
  query,
  queryData,
  rowClassName,
  rowKey,
}: AdminTableType<T>) => {
  const [filtered, setFiltered] = useState({ page: 0, size: 10 });
  const data = query({ ...queryData, ...filtered });
  const pageAble = data.data?.data;
  const [total, setTotal] = useState(pageAble?.totalElements || 1);

  const onFilter = (keys: object) => {
    setFiltered((prevState) => ({
      ...prevState,
      ...keys,
    }));
  };

  useEffect(() => {
    if (!data.isLoading) {
      setTotal(pageAble?.totalElements || 1);

      if (filtered.page + 1 > (pageAble?.totalPages || 1)) {
        onFilter({ page: pageAble?.totalPages || 0 });
      }
    }
  }, [data]);

  return (
    <Table
      className="table-custom-options"
      columns={columns}
      components={
        filters && {
          header: {
            row: (props) => (
              <TableHeader
                props={props}
                tableFilterProps={{ filtered, filters: filters, onFilter }}
              />
            ),
          },
        }
      }
      dataSource={pageAble?.content}
      loading={{
        indicator: (
          <Icon className="text-xl" component={(props) => <ChessKnightIcon {...props} />} spin />
        ),
        spinning: data.isLoading,
      }}
      locale={{
        emptyText: data.isLoading ? (
          <div className="p-2">
            <Skeleton active round />
            <Skeleton active round />
          </div>
        ) : (
          <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
      }}
      onChange={(pagination) => {
        onFilter({
          page: pagination.current ? pagination.current - 1 : 0,
          size: pagination.pageSize || 10,
        });
      }}
      pagination={{
        current: filtered.page + 1,
        defaultCurrent: 1,
        hideOnSinglePage: false,
        pageSize: filtered.size,
        position: position || ['topLeft', 'bottomLeft'],
        responsive: true,
        selectComponentClass: TableSizeChanger,
        showSizeChanger: true,
        total,
      }}
      rowClassName={rowClassName}
      rowKey={rowKey?.toString() ?? 'id'}
      scroll={{ x: true }}
      showSorterTooltip={false}
    />
  );
};
